import { useState, type FC } from 'react';
import { motion, type Variants } from 'framer-motion';

import {
  isCardMascotPreset,
  isFloorMascotPreset,
  type MascotAnchor,
} from '#/lib/mascot';
import type {
  SanityCardGrid,
  SanityCardGridCard,
} from '#/lib/sanity/queries/cardGrid';
import {
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';
import { BasicText } from '#/components/BasicText';
import { SectionMascot } from '#/components/BlobMascot';
import { ButtonRow } from '#/components/ButtonRow';
import { CardsContainer } from '#/components/CardsContainer';
import { formatHeadline } from '#/components/Headline';
import { SvgIcon } from '#/components/SvgIcon';

// Scroll reveal is scoped to this component via framer-motion. The intro and
// the cards container are each a single `whileInView` trigger that staggers its
// own children in (fade + up) using motion's built-in `staggerChildren`.
const REVEAL_VIEWPORT = { once: true, margin: '-150px' } as const;

const introGroup: Variants = {
  initial: {},
  animate: { transition: { delayChildren: 0.1, staggerChildren: 0.1 } },
};

const cardsGroup: Variants = {
  initial: {},
  animate: { transition: { staggerChildren: 0.25 } },
};

const fadeUp: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

// Step columns up gradually so cards never jump straight to a cramped count.
//   2-col → 1, 2
//   3-col → 1, 2 (through 1200), 3 at ≥1200
//   4-col → 1, 2, 3 at ≥1024, 4 at ≥1200
const COLUMN_CLASSES: Record<SanityCardGrid['columns'], string> = {
  '2': 'm:grid-cols-2',
  '3': 'm:grid-cols-2 lx:grid-cols-3',
  '4': 'm:grid-cols-2 l:grid-cols-3 lx:grid-cols-4',
};

function Card({
  card,
  theme,
  restackAtM,
  className,
  onRevealed,
}: {
  card: SanityCardGridCard;
  theme: SanityCardGrid['theme'];
  restackAtM: boolean;
  className?: string;
  /** Fires once the scroll-reveal (fade + up) has finished for this card. */
  onRevealed?: () => void;
}) {
  const isDark = isDarkSectionTheme(theme);
  const hasBackground = !card.removeCardBackground;
  const accentClass = getSectionThemeAccent(theme);
  const buttonTheme = isDark ? 'dark' : 'light';

  return (
    <motion.div
      variants={fadeUp}
      onAnimationComplete={
        onRevealed
          ? (definition) => {
              if (definition === 'animate') onRevealed();
            }
          : undefined
      }
      className={cn(
        '@container flex h-full flex-col gap-20 m:gap-28 rounded-16 p-20 m:p-30 l:p-40',
        hasBackground && (isDark ? 'bg-grayscale-06' : 'bg-cloud'),
        className,
      )}
    >
      {card.icon?.svg ? (
        <SvgIcon
          svg={card.icon.svg}
          className={cn('shrink-0 [&_svg]:size-40', accentClass)}
        />
      ) : null}

      {card.eyebrow ? (
        <p className={cn('type-eyebrow', accentClass)}>{card.eyebrow}</p>
      ) : null}

      {card.title ? (
        <h3
          className={cn(
            'type-heading6 m:type-heading4',
            isDark ? 'text-cloud' : 'text-charcoal',
          )}
        >
          {card.title}
        </h3>
      ) : null}

      {card.description ? (
        <BasicText
          value={card.description}
          className={cn(
            'type-paragraph m:type-paragraph-lg',
            isDark ? 'text-grayscale-03' : 'text-charcoal',
          )}
        />
      ) : null}

      {card.buttons?.length ? (
        <ButtonRow
          buttons={card.buttons}
          theme={buttonTheme}
          restackAtM={restackAtM}
        />
      ) : null}
    </motion.div>
  );
}

export const CardGrid: FC<SanityCardGrid> = (props) => {
  const isDark = isDarkSectionTheme(props.theme);
  const isCentered = props.titleAlignment === 'center';
  const highlightClass = getSectionThemeAccent(props.theme);
  // 3- and 4-column sections become multi-column at the `m` viewport breakpoint;
  // from there their (narrow) cards should always keep buttons stacked.
  const restackAtM = props.columns !== '2';
  // Card peers render inside host cards; bounce / roll / etc. are section-level.
  const mascotInCards = isCardMascotPreset(props.mascot);
  // Bounce & roll need a slim floor lane under the cards.
  const mascotOnFloor = isFloorMascotPreset(props.mascot);
  const bounceOnCards = props.mascot === 'bounce-teal-bottom-to-cta';
  // Left-edge steps host on the first card. Right-edge steps host on the last
  // card of the first row so they always peek near the grid's right edge;
  // 3-col grids are 2 columns until `lx`, so they use the 2nd card below `lx`
  // and the 3rd from `lx` up (one hidden per breakpoint). Clamped for grids
  // with fewer cards than columns.
  const lastIndex = props.cards.length - 1;
  const rightEdgeHosts: Record<number, string | undefined> =
    props.columns === '3' && lastIndex >= 2
      ? { 1: 'contents lx:hidden', 2: 'hidden lx:contents' }
      : { [Math.min(Number(props.columns) - 1, lastIndex)]: undefined };
  const mascotHosts: Record<
    number,
    Array<{ anchors: MascotAnchor[]; className?: string }>
  > = {};
  if (mascotInCards) {
    mascotHosts[0] = [{ anchors: ['card-left'] }];
    for (const [index, className] of Object.entries(rightEdgeHosts)) {
      (mascotHosts[Number(index)] ??= []).push({
        anchors: ['card-right', 'section-first-card'],
        className,
      });
    }
  }
  // The blob hides *behind* the card, so it must not mount until the card's
  // fade-up reveal has finished — otherwise it shows through the still-
  // transparent card.
  const [revealedCards, setRevealedCards] = useState<Record<number, true>>({});

  return (
    <div
      className={cn(
        // Card peers slide out past the outer cards; clip so they can never
        // widen the page, without turning the section into a scroll container.
        'relative overflow-x-clip p-main pt-40 m:pt-60 l:pt-100',
        // Slim floor lane for bounce / roll (blob ~120px) — not a full empty band.
        mascotOnFloor ? 'pb-64 m:pb-80 l:pb-100' : 'pb-40 m:pb-60 l:pb-100',
        getSectionThemeSurface(props.theme),
      )}
      {...(bounceOnCards ? { 'data-mascot-bounce': 'cards' } : null)}
    >
      {mascotInCards ? null : <SectionMascot preset={props.mascot} />}
      <div className="relative z-[1] mx-auto flex max-w-1235 flex-col gap-20 m:gap-48">
        {(props.eyebrow || props.title || props.subtitle) && (
          <motion.div
            className={cn(
              'flex max-w-900 flex-col gap-20',
              isCentered && 'mx-auto items-center text-center',
              // Keep the intro legible if a first-card peer rises into it.
              mascotInCards && 'relative z-[2]',
            )}
            variants={introGroup}
            initial="initial"
            whileInView="animate"
            viewport={REVEAL_VIEWPORT}
          >
            {props.eyebrow ? (
              <motion.p
                variants={fadeUp}
                className={cn('type-eyebrow', highlightClass)}
              >
                {props.eyebrow}
              </motion.p>
            ) : null}
            {props.title ? (
              <motion.h2
                variants={fadeUp}
                className="type-heading5 m:type-heading3"
              >
                {formatHeadline(props.title, highlightClass)}
              </motion.h2>
            ) : null}
            {props.subtitle ? (
              <motion.div variants={fadeUp}>
                <BasicText
                  value={props.subtitle}
                  className={cn(
                    'type-paragraph',
                    isDark ? 'text-grayscale-03' : 'text-grayscale-05',
                  )}
                />
              </motion.div>
            ) : null}
          </motion.div>
        )}

        <CardsContainer
          className={cn(
            // Single column: cap the width so cards don't stretch too wide, and
            // center them. The cap is lifted once the grid is multi-column (`m`).
            'mx-auto grid w-full max-w-[469px] grid-cols-1 gap-20 m:max-w-none m:gap-32',
            COLUMN_CLASSES[props.columns],
          )}
          variants={cardsGroup}
          initial="initial"
          whileInView="animate"
          viewport={REVEAL_VIEWPORT}
          {...(bounceOnCards ? { 'data-mascot-bounce-target': '' } : null)}
        >
          {props.cards.map((card, index) =>
            index in mascotHosts ? (
              // Blob sits under the card (z-0 vs z-1) so its hidden state is
              // fully covered by the card background and it peers out past
              // the card's edge. Variants still propagate through the wrapper.
              <div key={card._key} className="relative h-full">
                {revealedCards[index]
                  ? mascotHosts[index].map((host) => (
                      <div key={host.anchors[0]} className={host.className}>
                        <SectionMascot
                          preset={props.mascot}
                          anchors={host.anchors}
                        />
                      </div>
                    ))
                  : null}
                <Card
                  card={card}
                  theme={props.theme}
                  restackAtM={restackAtM}
                  className="relative z-[1]"
                  onRevealed={() =>
                    setRevealedCards((prev) => ({ ...prev, [index]: true }))
                  }
                />
              </div>
            ) : (
              <Card
                key={card._key}
                card={card}
                theme={props.theme}
                restackAtM={restackAtM}
              />
            ),
          )}
        </CardsContainer>
      </div>
    </div>
  );
};
